import React from 'react';
import { Card, CardContent, Typography, Box, LinearProgress, useTheme } from '@mui/material';
import { Task } from '../../types';

interface TaskSummaryProps {
  tasks: Task[];
}

export const TaskSummary: React.FC<TaskSummaryProps> = ({ tasks }) => {
  const theme = useTheme();

  const completed = tasks.filter(t => t.status === 'completed').length;
  const inProgress = tasks.filter(t => t.status === 'in-progress').length;
  const pending = tasks.filter(t => t.status === 'pending').length;
  const blocked = tasks.filter(t => t.status === 'blocked').length;
  const total = tasks.length;

  const getProgressColor = (status: string) => {
    switch (status) {
      case 'completed':
        return theme.palette.success.main;
      case 'in-progress':
        return theme.palette.info.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'blocked':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[300];
    }
  };

  const getProgressBackground = (status: string) => {
    switch (status) {
      case 'completed':
        return theme.palette.success.light;
      case 'in-progress':
        return theme.palette.info.light;
      case 'pending':
        return theme.palette.warning.light;
      case 'blocked':
        return theme.palette.error.light;
      default:
        return theme.palette.grey[100];
    }
  };

  const statusItems = [
    { label: 'Completed', count: completed, status: 'completed' },
    { label: 'In Progress', count: inProgress, status: 'in-progress' },
    { label: 'Pending', count: pending, status: 'pending' },
    { label: 'Blocked', count: blocked, status: 'blocked' },
  ];

  return (
    <Card 
      elevation={0}
      sx={{ 
        background: theme.palette.mode === 'light'
          ? 'linear-gradient(135deg, rgba(30, 58, 95, 0.03), rgba(45, 78, 125, 0.05))'
          : 'linear-gradient(135deg, rgba(19, 40, 68, 0.2), rgba(30, 58, 95, 0.3))',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <CardContent>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Task Summary
          <Typography 
            component="span" 
            variant="body2" 
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {total} total
          </Typography>
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {statusItems.map(({ label, count, status }) => (
            <Box key={status}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  mb: 0.5,
                }}
              >
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  {label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      color: getProgressColor(status),
                      lineHeight: 1,
                    }}
                  >
                    {count}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {Math.round((count / total) * 100)}%
                  </Typography>
                </Box>
              </Box>
              <Box 
                sx={{ 
                  width: '100%',
                  height: 8,
                  bgcolor: getProgressBackground(status),
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${(count / total) * 100}%`,
                    height: '100%',
                    bgcolor: getProgressColor(status),
                    borderRadius: 'inherit',
                    transition: 'width 0.5s ease-in-out',
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};